import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, ChevronsLeftRight, ArrowUpRight, Expand } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/shared";
import { PROJECTS } from "@/data/content";
import { waLink } from "@/lib/site";

function BeforeAfter({ before, after }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-[16/10] w-full cursor-ew-resize select-none overflow-hidden touch-none"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons > 0) update(e.clientX);
      }}
    >
      <img src={after} alt="Depois — projeto concluído pela Realiza Engenharia" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt="Antes — estado original da edificação" className="h-full w-full object-cover" loading="lazy" />
      </div>
      <span className="absolute left-4 top-4 rounded-full bg-navy-950/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-200 backdrop-blur">
        Antes
      </span>
      <span className="absolute right-4 top-4 rounded-full bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-navy-950">
        Depois
      </span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-gold" />
        <span className="absolute top-1/2 -ml-6 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-gold bg-navy-950/90 text-gold shadow-lg backdrop-blur">
          <ChevronsLeftRight className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

function ProjectModal({ project, onClose }) {
  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-950/92 p-4 backdrop-blur-md sm:p-8"
      onClick={onClose}
      data-testid="project-modal"
    >
      <motion.article
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ duration: 0.45, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        data-lenis-prevent
        className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto border border-gold/25 bg-navy-900"
      >
        <button
          onClick={onClose}
          data-testid="project-modal-close"
          aria-label="Fechar detalhes do projeto"
          className="sticky left-full top-4 z-10 mr-4 flex h-10 w-10 -translate-y-4 items-center justify-center rounded-full border border-white/20 bg-navy-950/80 text-white transition-colors hover:border-gold hover:text-gold"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="px-6 pb-8 sm:px-10">
          {project.before && project.after && (
            <BeforeAfter before={project.before} after={project.after} />
          )}
          {!project.before && (
            <img src={project.cover} alt={project.name} className="aspect-[16/10] w-full object-cover" loading="lazy" />
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-gold/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
              {project.category}
            </span>
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-slate-400">
              <MapPin className="h-3.5 w-3.5 text-gold" /> {project.location}
            </span>
          </div>
          <h3 className="mt-4 font-display text-3xl font-extrabold uppercase leading-tight tracking-wide text-white sm:text-4xl">
            {project.name}
          </h3>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold/80">{project.service}</p>
          <p className="mt-5 leading-relaxed text-slate-400">{project.description}</p>
          {project.during && (
            <img src={project.during} alt={`Durante a execução — ${project.name}`} className="mt-6 aspect-[16/9] w-full object-cover" loading="lazy" />
          )}
          {project.gallery && (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {project.gallery.map((src, i) => (
                <img key={src} src={src} alt={`${project.name} — registro de obra ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full border border-white/10 object-cover" />
              ))}
            </div>
          )}
          <div className="mt-6 border-l-2 border-gold bg-white/[0.03] p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Resultado</p>
            <p className="mt-2 font-serif text-lg italic leading-snug text-slate-200">{project.result}</p>
          </div>
          <a
            href={waLink(`Olá! Vi o projeto "${project.name}" no site da Realiza Engenharia e quero um orçamento parecido.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="project-modal-cta"
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold tracking-[0.14em] text-navy-950 transition-all duration-300 hover:bg-gold-bright sm:w-auto sm:px-10"
          >
            QUERO UM PROJETO ASSIM <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </motion.article>
    </motion.div>
  );
}

export default function Projects() {
  const [filter, setFilter] = useState("TODOS");
  const [selected, setSelected] = useState(null);
  const categories = ["TODOS", ...new Set(PROJECTS.map((p) => p.category))];
  const visible = filter === "TODOS" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="projetos" className="scroll-mt-20 bg-navy-900/40 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Portfólio"
          title={<>Projetos <span className="text-gold-grad">realizados</span></>}
          sub="Obras que traduzem nosso método: planejamento, execução fiscalizada e resultado registrado."
        />
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`project-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                  filter === cat
                    ? "bg-gold text-navy-950"
                    : "border border-white/15 text-slate-400 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid gap-5 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {visible.map((project, i) => (
              <motion.article
                layout
                key={project.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: EASE }}
                data-testid={`project-card-${project.id}`}
                onClick={() => setSelected(project)}
                className={`group relative cursor-pointer overflow-hidden border border-white/[0.07] ${
                  i === visible.length - 1 && visible.length % 2 === 1 ? "md:col-span-2" : ""
                }`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={project.cover}
                    alt={`Projeto ${project.name} — ${project.service}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="rounded-full border border-gold/40 bg-navy-950/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur">
                      {project.category}
                    </span>
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300">
                      <MapPin className="h-3 w-3 text-gold" /> {project.location}
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight tracking-wide text-white">
                    {project.name}
                  </h3>
                  <p className="mt-1 text-xs text-slate-400">{project.service}</p>
                  <span className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold opacity-0 transition-all duration-300 group-hover:opacity-100">
                    <Expand className="h-3.5 w-3.5" /> VER PROJETO
                  </span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
